import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import "./Sidebar.css";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { getUserRole, USER_ROLES, hasPermission, PERMISSIONS } from "../../utils/UsersPermissions";

import { API_URL } from "../../utils/config"; // Import the API URL
const baseUrl = API_URL;
// Updated menuItems without the /dashboard prefix (except Dashboard itself)
const menuItems = [
  { path: "/dashboard", icon: "/activity.svg", label: "Dashboard" },
  { path: "/suppliers", icon: "/shop.svg", label: "Suppliers" },
  { path: "/products", icon: "/truck.svg", label: "Products" },
  { path: "/orders", icon: "/receipt-2.svg", label: "Orders" },
  { path: "/organizers", icon: "/user-octagon.svg", label: "Organizers" },
  { path: "/messages", icon: "/activity.svg", label: "Messages" },
  { path: "/reviews", icon: "/heart.svg", label: "Reviews" },
  { path: "/settings", icon: "/setting-2.svg", label: "Settings" },
];

const Sidebar = ({ menuToggle, setMenuToggle }) => {
  const { t, i18n } = useTranslation("sidebar");
  const navigate = useNavigate();
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1280);
  const [orderCount, setOrderCount] = useState("");
  const [messageCount, setMessageCount] = useState("");
  const { isAuthenticated ,userRole} = useAuth();

  const canAccessChat = hasPermission(PERMISSIONS.ACCESS_CHAT);

  const isRTL = i18n.language === "ar";

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      setIsTablet(window.innerWidth <= 1280);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch order count
  const fetchOrderCount = async () => {
    try {
      if (!isAuthenticated) {
        navigate("/login");
        return 0;
      }
      const response = await fetch(`${baseUrl}/api/v1/attnd-supplier/suppliers/newOrders`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      });

      if (response.status === 401) {
        localStorage.removeItem("user");
        navigate("/login");
        throw new Error("Unauthorized access. Please log in again.");
      }

      if (!response.ok) {
        throw new Error("Failed to fetch orders");
      }

      const data = await response.json();
      return data.count || 0;
    } catch (error) {
      console.error("Error fetching order count:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
      }
      return 0;
    }
  };

  // Fetch messages count
  const fetchMessageCount = async () => {
    try {
      if (!isAuthenticated) {
        navigate("/login");
        return 0;
      }
      const response = await fetch(`${baseUrl}/api/v1/chat/newMessages/supplier`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      });

      if (response.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
        throw new Error("Unauthorized access. Please log in again.");
      }

      if (!response.ok) {
        throw new Error("Failed to fetch orders");
      }

      const data = await response.json();
      return data.count || 0;
    } catch (error) {
      console.error("Error fetching order count:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
      }
      return 0;
    }
  };

  useEffect(() => {
    fetchOrderCount().then((count) => setOrderCount(count));
    
    // Only fetch message count if user can access chat
    if (canAccessChat) {
      fetchMessageCount().then((count) => setMessageCount(count));
    }

    const orderInterval = setInterval(() => {
      fetchOrderCount().then((count) => setOrderCount(count));
    }, 60000);

    let messageInterval;
    if (canAccessChat) {
      messageInterval = setInterval(() => {
        fetchMessageCount().then((count) => setMessageCount(count));
      }, 60000);
    }

    return () => {
      clearInterval(orderInterval);
      if (messageInterval) {
        clearInterval(messageInterval);
      }
    };
  }, [canAccessChat]);

  const menuStyle = {
    [isRTL ? "right" : "left"]: isTablet ? (menuToggle ? "0" : "-250px") : "0",
    
    direction: isRTL ? "rtl" : "ltr",
    borderRight: isRTL ? "none" : "1px solid #E5E7EB",
    borderLeft: isRTL ? "1px solid #E5E7EB" : "none",
  };

  const handleMessageClick = () => {
    setMessageCount("");
    setMenuToggle(false);
  };

  return (
    <div className="sidebar-sidebar" style={menuStyle}>
      <ul className="sidebar-content">
        {menuItems.map((item) => {
          const key = item.label.toLowerCase();
          const label = t(`menu.${key}`);
          const alt = t("iconAlt", { item: label });

          // Skip messages menu item if user doesn't have chat permission
          if (label === t("menu.messages") && !canAccessChat) {
            return null;
          }

          // Skip organizers menu item if user is viewer or manager (only suppliers can see it)
          if (label === t("menu.organizers") && (userRole === 'viewer' || userRole === 'manager')) {
            return null;
          }

          if (label !== t("menu.orders") && label !== t("menu.messages")) {
            return (
              <li key={item.path}>
                <NavLink
                  onClick={() => setMenuToggle(false)}
                  to={item.path}
                  className={({ isActive }) =>
                    isActive ? "link-dashboard-active" : "link-dashboard"
                  }
                >
                  <img className="dashboard-icon" src={item.icon} alt={alt} />
                  {label}
                </NavLink>
              </li>
            );
          }

          if (label === t("menu.orders")) {
            return (
              <li key={item.path}>
                <NavLink
                  onClick={() => {
                    setOrderCount("");
                    setMenuToggle(false);
                  }}
                  to={item.path}
                  className={({ isActive }) =>
                    `relative ${
                      isActive ? "link-dashboard-active" : "link-dashboard"
                    }`
                  }
                >
                  <img className="dashboard-icon" src={item.icon} alt={alt} />
                  {label}
                  {(Array.isArray(orderCount)
                    ? orderCount.length > 0
                    : orderCount > 0) && (
                    <span className="unread-indicator">{orderCount}</span>
                  )}
                </NavLink>
              </li>
            );
          }

          return (
            <li key={item.path}>
              <NavLink
                onClick={handleMessageClick}
                to={item.path}
                className={({ isActive }) =>
                  `relative ${
                    isActive ? "link-dashboard-active" : "link-dashboard"
                  }`
                }
              >
                <img className="dashboard-icon" src={item.icon} alt={alt} />
                {label}
                {messageCount > 0 && (
                  <span className="unread-indicator">{messageCount}</span>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default Sidebar;
